import {createSelector} from 'reselect';
import _, {every, flatten, isEmpty, keys} from 'lodash';

import {
  getAssociableAgreements,
  getAssociatedAgreements,
  getAssociatedAgreementsBuyBank,
  getPendingStatusAgreements,
} from '../associatedAgreements/associatedAgreements.selectors';
import {
  ACCOUNT_TYPES,
  CUSTOM_DISTRIBUTION_METHOD_STEP,
  EXISTING_AUTO_EXCHANGE,
  EXISTING_AUTO_SELL,
  NON_RETIREMENT,
  RETIREMENT,
  RETURN_MAIL_VERIFY_ADDRESS,
} from '../../../settings/settings';
import {
  getExistingAutomatics
} from '../existingAutomatics/existingAutomatics.selectors';
import {
  checkForNonRetirementUnLockFunds,
  checkFoundFeaturesByCode,
  sortingFn,
  updateLockedList,
  updateNonRetirementLockedList,
} from '../../../utils/fund-utils';
import {
  getSelectedBank,
  getSelectedDestination,
  getSelectedDistribution,
} from '../shoppingCart/shoppingCart.selectors';
import {
  getExistingRMDinfo,
  getRMDAgreements,
} from '../RMD/RMDSetting.selectors';
import {
  getPrimaryAddress,
  getUserBPID,
} from '../PersonalInformation/personalInformation.selectors';
import {getAllFundsList} from '../allFunds/allFunds.selectors';
import {getCurrentStep} from '../applicationData/applicationData.selectors';

export const getUserFunds = (state) => {
  if (state.userFunds.funds && state.userFunds.funds.length) {
    return state.userFunds.funds[0] ? state.userFunds.funds[0] : {};
  }
};

export const getBalanceAsOfDate = (state) => {
  if (state.userFunds.funds && state.userFunds.funds.length) {
    return state.userFunds.funds[0].balanceAsOfDate
      ? state.userFunds.funds[0].balanceAsOfDate
      : {};
  }
  return {};
};

export const getUserRetirementFunds = (state) => {
  const retirementAgreements =
    state.userFunds?.funds?.[0]?.retirementAgreements;
  const businessPartyId = state.personalInformation?.data?.businessPartyId;
  if (retirementAgreements) {
    const filteredRetirementAgreements = {};
    Object.keys(retirementAgreements).forEach((k) => {
      const filteredAgreements = retirementAgreements[k].filter((a) => {
        return a.agreementParties?.find(
          (ap) =>
            ap.roleCode === 'OWNR' && ap.businessPartyID === businessPartyId
        );
      });
      filteredRetirementAgreements[k] = filteredAgreements;
    });
    return filteredRetirementAgreements;
  }
  return {};
};

export function getNonRetirementFunds(state) {
  if (state.userFunds.funds && state.userFunds.funds.length) {
    return state.userFunds.funds[0].nonRetirementAgreements
      ? state.userFunds.funds[0].nonRetirementAgreements
      : {};
  } else {
    return {};
  }
}

export const getOWNEDNonRetirementFunds = createSelector(
  getNonRetirementFunds,
  getUserBPID,
  (userFunds, bpid) => {
    let filteredFunds = {
      INDIVIDUAL: userFunds['INDIVIDUAL']
        ? userFunds['INDIVIDUAL']?.filter(
          (fund) =>
            fund.agreementParties?.filter(
              (party) =>
                party.roleCode === 'OWNR' && party.businessPartyID === bpid
            ).length > 0
        )
        : [],
      JOINT_TENANT: userFunds['JOINT_TENANT']
        ? userFunds['JOINT_TENANT']?.filter(
          (fund) =>
            fund.agreementParties?.filter(
              (party) =>
                party.roleCode === 'OWNR' && party.businessPartyID === bpid
            ).length > 0
        )
        : [],
      TRANSFER_ON_DEATH: userFunds['TRANSFER_ON_DEATH']
        ? userFunds['TRANSFER_ON_DEATH']?.filter(
          (fund) =>
            fund.agreementParties?.filter(
              (party) =>
                party.roleCode === 'OWNR' && party.businessPartyID === bpid
            ).length > 0
        )
        : [],
      ROTH_IRA_DECEASED_BENE: userFunds['ROTH_IRA_DECEASED_BENE']
        ? userFunds['ROTH_IRA_DECEASED_BENE']?.filter(
          (fund) =>
            fund.agreementParties?.filter(
              (party) =>
                party.roleCode === 'OWNR' && party.businessPartyID === bpid
            ).length > 0
        )
        : [],
      TRADITIONAL_IRA_DECEASED_BENE: userFunds['TRADITIONAL_IRA_DECEASED_BENE']
        ? userFunds['TRADITIONAL_IRA_DECEASED_BENE']?.filter(
          (fund) =>
            fund.agreementParties?.filter(
              (party) =>
                party.roleCode === 'OWNR' && party.businessPartyID === bpid
            ).length > 0
        )
        : [],
    };
    return filteredFunds;
  }
);

export const getExchangeToFunds = (state) =>
  state.userFunds.exchangeFunds.traditionalFunds
    .concat(state.userFunds.exchangeFunds.rolloverFunds)
    .map((fund) => fund.agreementID);
export const getExchangeLoaded = (state) => state.userFunds.isExchangeFundsSet;

export const isReinvestmentEmpty = createSelector(
  getExchangeToFunds,
  (userFund) => {
    const len = userFund.length;
    return len === 0;
  }
);

export const getUserNonRetirementFunds = createSelector(
  getOWNEDNonRetirementFunds,
  getExchangeToFunds,
  (state) => state.userFunds.isExchangeFundsLoaded,
  getAllFundsList,
  (userFunds, exchangeFunds, isLoaded, allFunds) => {
    let filteredFunds = {
      INDIVIDUAL: userFunds['INDIVIDUAL']
        ? userFunds['INDIVIDUAL'].filter((fund) =>
          exchangeFunds.includes(fund.agreementID)
        )
        : [],
      JOINT_TENANT: userFunds['JOINT_TENANT']
        ? userFunds['JOINT_TENANT'].filter((fund) =>
          exchangeFunds.includes(fund.agreementID)
        )
        : [],
      TRANSFER_ON_DEATH: userFunds['TRANSFER_ON_DEATH']
        ? userFunds['TRANSFER_ON_DEATH'].filter((fund) =>
          exchangeFunds.includes(fund.agreementID)
        )
        : [],
      ROTH_IRA_DECEASED_BENE: userFunds['ROTH_IRA_DECEASED_BENE']
        ? userFunds['ROTH_IRA_DECEASED_BENE'].filter((fund) =>
          exchangeFunds.includes(fund.agreementID)
        )
        : [],
      TRADITIONAL_IRA_DECEASED_BENE: userFunds['TRADITIONAL_IRA_DECEASED_BENE']
        ? userFunds['TRADITIONAL_IRA_DECEASED_BENE'].filter((fund) =>
          exchangeFunds.includes(fund.agreementID)
        )
        : [],
    };
    filteredFunds.INDIVIDUAL = filteredFunds.INDIVIDUAL.map((fund) => {
      const temp = allFunds?.filter((allFund) => {
        return fund.ticker === allFund.ticker;
      });
      if (temp?.length > 0) {
        return { ...fund, terminated: temp[0].terminated };
      } else {
        return { ...fund };
      }
    });
    filteredFunds.JOINT_TENANT = filteredFunds.JOINT_TENANT.map((fund) => {
      const temp = allFunds?.filter((allFund) => {
        return fund.ticker === allFund.ticker;
      });
      if (temp?.length > 0) {
        return { ...fund, terminated: temp[0].terminated };
      } else {
        return { ...fund };
      }
    });
    filteredFunds.TRANSFER_ON_DEATH = filteredFunds.TRANSFER_ON_DEATH.map(
      (fund) => {
        const temp = allFunds?.filter((allFund) => {
          return fund.ticker === allFund.ticker;
        });
        if (temp?.length > 0) {
          return { ...fund, terminated: temp[0].terminated };
        } else {
          return { ...fund };
        }
      }
    );
    filteredFunds.ROTH_IRA_DECEASED_BENE =
      filteredFunds.ROTH_IRA_DECEASED_BENE.map((fund) => {
        const temp = allFunds?.filter((allFund) => {
          return fund.ticker === allFund.ticker;
        });
        if (temp?.length > 0) {
          return { ...fund, terminated: temp[0].terminated };
        } else {
          return { ...fund };
        }
      });
    filteredFunds.TRADITIONAL_IRA_DECEASED_BENE =
      filteredFunds.TRADITIONAL_IRA_DECEASED_BENE.map((fund) => {
        const temp = allFunds?.filter((allFund) => {
          return fund.ticker === allFund.ticker;
        });
        if (temp?.length > 0) {
          return { ...fund, terminated: temp[0].terminated };
        } else {
          return { ...fund };
        }
      });
    return isLoaded ? filteredFunds : userFunds;
  }
);

export const getUsersRetirementFunds = createSelector(
  getUserRetirementFunds,
  getExistingAutomatics,
  getExistingRMDinfo,
  getUserBPID,
  getAllFundsList,
  (funds, existingAuto, existingRMD, BPID, allFunds) => {
    let { ROLLOVER_IRA = [], TRADITIONAL_IRA = [] } = funds;
    const RMDAgreements =
      existingRMD && existingRMD && existingRMD.length > 0 ? existingRMD : [];

    ROLLOVER_IRA = ROLLOVER_IRA.map((fund) => {
      const temp = allFunds?.filter((allFund) => {
        return fund.ticker === allFund.ticker;
      });
      if (temp?.length > 0) {
        return { ...fund, terminated: temp[0].terminated };
      } else {
        return { ...fund };
      }
    });
    TRADITIONAL_IRA = TRADITIONAL_IRA.map((fund) => {
      const temp = allFunds?.filter((allFund) => {
        return fund.ticker === allFund.ticker;
      });
      if (temp?.length > 0) {
        return { ...fund, terminated: temp[0].terminated };
      } else {
        return { ...fund };
      }
    });
    const mapIRAS = (iras) =>
      iras.map((fund) => {
        const potentialRMDAgreement = RMDAgreements.find(
          (rmdFund) =>
            rmdFund?.sourceAgreement?.agreementKey?.agreementID ===
            fund.agreementID
        );
        const hasAutoExchange =
            Boolean(existingAuto.autoExchanges[fund.agreementID]) &&
            existingAuto.autoExchanges[fund.agreementID].some(
              (temp) => temp.disbursementType.enumerationID !== 11
            ) &&
            !potentialRMDAgreement,
          hasAutoSell =
            Boolean(existingAuto.autoSells[fund.agreementID]) &&
            existingAuto.autoSells[fund.agreementID].some(
              (temp) => temp.disbursementType.enumerationID !== 11
            ) &&
            !potentialRMDAgreement;
        return {
          ...fund,
          hasAutoExchange,
          hasAutoSell,
          hasRMDAgreement: Boolean(potentialRMDAgreement),
          rmdType: potentialRMDAgreement?.rmdType,
        };
      });
    return {
      ROLLOVER_IRA: mapIRAS(ROLLOVER_IRA),
      TRADITIONAL_IRA: mapIRAS(TRADITIONAL_IRA),
    };
  }
);
export const getAllUserFunds = createSelector(
  getUserFunds,
  ({ nonRetirementAgreements, retirementAgreements }) => {
    let funds = [];
    nonRetirementAgreements &&
    Object.keys(nonRetirementAgreements).forEach((fund) => {
      funds = [...funds, ...nonRetirementAgreements[fund]];
    });
    retirementAgreements &&
    Object.keys(retirementAgreements).forEach((fund) => {
      funds = [...funds, ...retirementAgreements[fund]];
    });
    return funds;
  }
);
export const getUserExistingTickers = createSelector(
  getAllUserFunds,
  (allFunds) => {
    return allFunds.map((fund) => fund.ticker);
  }
);

export const doesUserHaveAutomaticSellOrExchange = createSelector(
  getUsersRetirementFunds,
  (retirementFunds) => {
    const autoRollover = retirementFunds.ROLLOVER_IRA.some(
      (fund) => fund.hasAutoSell === true || fund.hasAutoExchange === true
    );
    const autoTraditional = retirementFunds.ROLLOVER_IRA.some(
      (fund) => fund.hasAutoSell === true || fund.hasAutoExchange === true
    );
    return autoRollover || autoTraditional;
  }
);

export const getNonRetirementFundsCount = createSelector(
  getUserNonRetirementFunds,
  (funds) => {
    const {
      INDIVIDUAL = [],
      JOINT_TENANT = [],
      TRANSFER_ON_DEATH = [],
      ROTH_IRA_DECEASED_BENE = [],
      TRADITIONAL_IRA_DECEASED_BENE = [],
    } = funds;
    return (
      INDIVIDUAL.length +
      JOINT_TENANT.length +
      TRANSFER_ON_DEATH.length +
      ROTH_IRA_DECEASED_BENE.length +
      TRADITIONAL_IRA_DECEASED_BENE.length
    );
  }
);

export const getRetirementFundsCount = createSelector(
  getUserRetirementFunds,
  (funds) => {
    const { ROLLOVER_IRA = [], TRADITIONAL_IRA = [] } = funds;
    return (
      ROLLOVER_IRA.filter((fund) => fund.equityAsOf !== 0).length +
      TRADITIONAL_IRA.filter((fund) => fund.equityAsOf !== 0).length
    );
  }
);

export const getSortedRetirementFunds = createSelector(
  getUsersRetirementFunds,
  getAssociatedAgreementsBuyBank,
  getSelectedDestination,
  getSelectedBank,
  getPendingStatusAgreements,
  (
    funds,
    associatedAgreements,
    destination,
    selectedBank,
    pendingStatusAgreements
  ) => {
    const { ROLLOVER_IRA = [], TRADITIONAL_IRA = [] } = funds;
    const correctBankID = selectedBank.bankAgreementID
      ? associatedAgreements[selectedBank.bankAgreementID]
      : [];
    ROLLOVER_IRA.map((fund) => {
      return updateLockedList(
        fund,
        correctBankID.associatedAgreements,
        destination,
        pendingStatusAgreements
      );
    }).sort(sortingFn);
    TRADITIONAL_IRA.map((fund) => {
      return updateLockedList(
        fund,
        correctBankID.associatedAgreements,
        destination,
        pendingStatusAgreements
      );
    }).sort(sortingFn);
    return { ROLLOVER_IRA, TRADITIONAL_IRA };
  }
);

export const getSortedNonRetirementFunds = createSelector(
  getUserNonRetirementFunds,
  (funds) => {
    const {
      INDIVIDUAL = [],
      JOINT_TENANT = [],
      TRANSFER_ON_DEATH = [],
      ROTH_IRA_DECEASED_BENE = [],
      TRADITIONAL_IRA_DECEASED_BENE = [],
    } = funds;
    const JOINT_TRANSFER_ON_DEATH = [
      ...ROTH_IRA_DECEASED_BENE,
      ...TRADITIONAL_IRA_DECEASED_BENE,
    ];
    INDIVIDUAL.sort(sortingFn);
    JOINT_TENANT.sort(sortingFn);
    TRANSFER_ON_DEATH.sort(sortingFn);
    JOINT_TRANSFER_ON_DEATH.sort(sortingFn);
    return {
      INDIVIDUAL,
      JOINT_TENANT,
      TRANSFER_ON_DEATH,
      JOINT_TRANSFER_ON_DEATH,
    };
  }
);

export const getNonRetirementLockedFunds = createSelector(
  getSortedNonRetirementFunds,
  ({
     INDIVIDUAL,
     JOINT_TENANT,
     TRANSFER_ON_DEATH,
     JOINT_TRANSFER_ON_DEATH,
   }) => {
    let individualLockedList = [];
    let jointLockedList = [];
    let transferLockedList = [];
    let jointTransferLockedList = [];
    for (let index = 0; index < INDIVIDUAL.length; index++) {
      const element = INDIVIDUAL[index];
      individualLockedList = updateNonRetirementLockedList(
        individualLockedList,
        element,
        []
      );
    }
    for (let index = 0; index < JOINT_TENANT.length; index++) {
      const element = JOINT_TENANT[index];
      jointLockedList = updateNonRetirementLockedList(jointLockedList, element);
    }
    for (let index = 0; index < TRANSFER_ON_DEATH.length; index++) {
      const element = TRANSFER_ON_DEATH[index];
      transferLockedList = updateNonRetirementLockedList(
        transferLockedList,
        element
      );
    }

    for (let index = 0; index < JOINT_TRANSFER_ON_DEATH.length; index++) {
      const element = JOINT_TRANSFER_ON_DEATH[index];
      jointTransferLockedList = updateNonRetirementLockedList(
        jointTransferLockedList,
        element
      );
    }
    return {
      individualLockedList,
      transferLockedList,
      jointLockedList,
      jointTransferLockedList,
    };
  }
);

export const getNonRetirementUnLockFunds = createSelector(
  getSortedNonRetirementFunds,
  ({
     INDIVIDUAL,
     JOINT_TENANT,
     TRANSFER_ON_DEATH,
     JOINT_TRANSFER_ON_DEATH,
   }) => {
    let individualUnLockedList = [];
    let jointUnLockedList = [];
    let transferUnLockedList = [];
    let jointTransferUnLockedList = [];
    individualUnLockedList = INDIVIDUAL.filter((fund) => {
      return checkForNonRetirementUnLockFunds(fund);
    });
    jointUnLockedList = JOINT_TENANT.filter((fund) => {
      return checkForNonRetirementUnLockFunds(fund);
    });

    transferUnLockedList = TRANSFER_ON_DEATH.filter((fund) => {
      return checkForNonRetirementUnLockFunds(fund);
    });

    jointTransferUnLockedList = JOINT_TRANSFER_ON_DEATH.filter((fund) => {
      return checkForNonRetirementUnLockFunds(fund);
    });
    return {
      individualUnLockedList,
      jointUnLockedList,
      transferUnLockedList,
      jointTransferUnLockedList,
    };
  }
);

export const getTraditionalOrRolloverIraFunds = createSelector(
  getUserRetirementFunds,
  (funds) => {
    return !isEmpty(funds.TRADITIONAL_IRA) || !isEmpty(funds.ROLLOVER_IRA);
  }
);

/**
 * Simple selector to return if a user owns retirement funds (unrelated to eligibility)
 * @param state
 * @returns {Boolean}
 *
 */
export const getHasRetirementFunds = hasFundsByAgreementFactory(RETIREMENT);

/**
 * Simple selector to return if a user owns non-retirement funds (unrelated to eligibility)
 * @param state - Internally uses hasFundByAgreementFactory
 * @returns {Boolean}
 *
 */
export const getHasTaxableFunds = hasFundsByAgreementFactory(NON_RETIREMENT);

/**
 * Factory method designed to take userFunds classifications and return whether or not the user owns funds in a
 * given classification
 * @param fundGroup - this takes an object map of agreement types found in userFunds
 * example: getUserRetirementFunds
 * @returns {Selector}
 *
 */
function hasFundsByAgreementFactory(fundGroup) {
  return createSelector(
    getUserRetirementFunds,
    getUserNonRetirementFunds,
    (retirementFunds, nonRetirementFunds) => {
      let result = null;
      const funds =
        fundGroup === RETIREMENT ? retirementFunds : nonRetirementFunds;
      // probably more appropriate as a switch after we support more than two classifications
      const agreementTypes = keys(funds);
      const fundsByGroupList = agreementTypes.map((agreementType) => {
        if (funds[agreementType] && funds[agreementType].length) {
          result = funds[agreementType];
        }
        return result;
      });
      return Boolean(fundsByGroupList.length);
    }
  );
}

export const getTraditionalIraAddresses = createSelector(
  getUserRetirementFunds,
  (userRetirementFunds) => {
    let addresses = [];
    if (
      !isEmpty(userRetirementFunds) &&
      !isEmpty(userRetirementFunds.TRADITIONAL_IRA)
    ) {
      addresses = userRetirementFunds.TRADITIONAL_IRA.filter(
        (fund) => fund.equityAsOf !== 0
      ).map((fund) => {
        return {
          ...fund.address,
          type: 'TRADITIONAL_IRA',
          serviceId: checkFoundFeaturesByCode(fund),
          restrictAddress: fund.restrictedByAddressChange,
        };
      });
    }
    return addresses;
  }
);

export const getRollOverIraAddresses = createSelector(
  getUserRetirementFunds,
  (userRetirementFunds) => {
    let addresses = [];
    if (
      !isEmpty(userRetirementFunds) &&
      !isEmpty(userRetirementFunds.ROLLOVER_IRA)
    ) {
      addresses = userRetirementFunds.ROLLOVER_IRA.filter(
        (fund) => fund.equityAsOf !== 0
      ).map((fund) => {
        return {
          ...fund.address,
          type: 'ROLLOVER_IRA',
          serviceId: checkFoundFeaturesByCode(fund),
          restrictAddress: fund.restrictedByAddressChange,
        };
      });
    }
    return addresses;
  }
);
export const doAddressesExist = createSelector(
  getRollOverIraAddresses,
  getTraditionalIraAddresses,
  getPrimaryAddress,
  (rollOverAddresses, traditionalAddresses, primaryAddress) => {
    let anyAddress = false;
    rollOverAddresses.forEach((addresses) => {
      if (addresses.address !== undefined) {
        anyAddress = true;
      }
    });
    traditionalAddresses.forEach((addresses) => {
      if (addresses.address !== undefined) {
        anyAddress = true;
      }
    });
    if (
      primaryAddress.fullName !== undefined ||
      primaryAddress.primaryAddress !== undefined ||
      anyAddress
    ) {
      return true;
    } else {
      return false;
    }
  }
);

export const getAddressChangeFlag = createSelector(
  getUserRetirementFunds,
  (userFundList) => {
    const traditionalList = userFundList.TRADITIONAL_IRA
      ? userFundList.TRADITIONAL_IRA
      : [];
    const rollOverList = userFundList.ROLLOVER_IRA
      ? userFundList.ROLLOVER_IRA
      : [];
    const newList = [...traditionalList, ...rollOverList];
    let flag = false;
    flag = newList.every((el) => {
      return el.restrictedByAddressChange === true;
    });
    return flag;
  }
);

export const getEffectiveDate = createSelector(
  getUserRetirementFunds,
  getAddressChangeFlag,
  (userFundList, effectiveAddressChangedFlag) => {
    const traditionalList = userFundList.TRADITIONAL_IRA
      ? userFundList.TRADITIONAL_IRA
      : [];
    const rollOverList = userFundList.ROLLOVER_IRA
      ? userFundList.ROLLOVER_IRA
      : [];
    const newList = [...traditionalList, ...rollOverList];
    return !effectiveAddressChangedFlag
      ? null
      : newList
        .filter((fund) => !fund.addressChangedSameDay)
        .map((f) => f.address.effectiveDate)
        .sort((a, b) => new Date(a) - new Date(b))[0];
  }
);

export const getReturnMailFlagForAddresses = createSelector(
  getUserRetirementFunds,
  (userFundList) => {
    const traditionalList = userFundList.TRADITIONAL_IRA
      ? userFundList.TRADITIONAL_IRA
      : [];
    const rollOverList = userFundList.ROLLOVER_IRA
      ? userFundList.ROLLOVER_IRA
      : [];
    const newList = [...traditionalList, ...rollOverList];

    let isReturnActive = true;
    isReturnActive = newList.every((fund) => {
      return checkFoundFeaturesByCode(fund, RETURN_MAIL_VERIFY_ADDRESS);
    });
    if (!isReturnActive) return isReturnActive;
    return isReturnActive;
  }
);

export const checkForAccountTypes = createSelector(
  getUserRetirementFunds,
  getUserNonRetirementFunds,
  (retirementFunds, nonRetirementFunds) => {
    const nonRetireFunds = nonRetirementFunds;
    const allFunds = {
      ...(retirementFunds || {}),
      ...(nonRetirementFunds || {}),
    };
    const availableAccounts = {};

    for (const account in ACCOUNT_TYPES) {
      if (allFunds[account] && !isEmpty(allFunds[account])) {
        availableAccounts[account] = allFunds[account];
      }
    }
    return isEmpty(nonRetireFunds) || isEmpty(availableAccounts)
      ? false
      : availableAccounts;
  }
);

export const checkForEligibleClosedAccounts = createSelector(
  checkForAccountTypes,
  (accounts) => {
    if (accounts) {
      const subAccounts = [];
      for (const account in accounts) {
        subAccounts.push(...accounts[account]);
      }

      try {
        return !subAccounts.every((subAcc) => {
          return parseInt(subAcc.balanceAsOf) === 0;
        });
      } catch (err) {
        return false;
      }
    }
    return false;
  }
);

export const getPuchaseBlockedForFund = createSelector(
  checkForAccountTypes,
  (accounts) => {
    const allAccounts = flatten(Object.values(accounts));
    return every(
      allAccounts,
      (fund) =>
        fund.purchaseBlocked &&
        fund.agreementType !== ACCOUNT_TYPES.TRADITIONAL_IRA_DECEASED_BENE &&
        fund.agreementType !== ACCOUNT_TYPES.ROTH_IRA_DECEASED_BENE
    );
  }
);

export const hasErrorFund = (state) => state.userFunds.hasError;

export const getSubmitProportionalRMDFunds = createSelector(
  getSortedRetirementFunds,
  ({ ROLLOVER_IRA, TRADITIONAL_IRA }) => {
    const filteredRollover = ROLLOVER_IRA.filter(
      (fund) =>
        !fund.lockedText ||
        fund.lockedText === EXISTING_AUTO_EXCHANGE ||
        fund.lockedText === EXISTING_AUTO_SELL
    ).filter(
      (fund) =>
        fund.terminated !== true &&
        fund.productType.toLowerCase() !== 'brokerage'
    )
    const filteredTransfer = TRADITIONAL_IRA.filter(
      (fund) =>
        !fund.lockedText ||
        fund.lockedText === EXISTING_AUTO_EXCHANGE ||
        fund.lockedText === EXISTING_AUTO_SELL
    ).filter(
      (fund) =>
        fund.terminated !== true &&
        fund.productType.toLowerCase() !== 'brokerage'
    )
    return filteredRollover.concat(filteredTransfer)
  }
)
export const getRMDfunds = createSelector(
  getSortedRetirementFunds,
  getSelectedDistribution,
  getCurrentStep,
  ({ ROLLOVER_IRA, TRADITIONAL_IRA }, distribution, currStep) => {
    const currentPage = currStep;
    const distributionPage = '/custom';
    const filteredRolloeverIRA = ROLLOVER_IRA.filter(
      (fund) =>
        fund.equityAsOf !== 0 && fund.productType.toLowerCase() !== 'brokerage'
    );
    const filteredTraditionalIRA = TRADITIONAL_IRA.filter(
      (fund) =>
        fund.equityAsOf !== 0 && fund.productType.toLowerCase() !== 'brokerage'
    );

    if (
      distribution === CUSTOM_DISTRIBUTION_METHOD_STEP &&
      currentPage === CUSTOM_DISTRIBUTION_METHOD_STEP
    ) {
      return {
        ROLLOVER_IRA: {
          unlockedFunds: ROLLOVER_IRA.filter((fund) => !fund.lockedText).filter(
            (fund) =>
              fund.equityAsOf !== 0 &&
              fund.terminated !== true &&
              fund.productType.toLowerCase() !== 'brokerage'
          ),
          lockedFunds: ROLLOVER_IRA.filter(
            (fund) => fund.lockedText || fund.terminated === true
          ).filter(
            (fund) =>
              fund.equityAsOf !== 0 &&
              fund.productType.toLowerCase() !== 'brokerage'
          ),
        },
        TRADITIONAL_IRA: {
          unlockedFunds: TRADITIONAL_IRA.filter(
            (fund) => !fund.lockedText && fund.terminated !== true
          ).filter(
            (fund) =>
              fund.equityAsOf !== 0 &&
              fund.productType.toLowerCase() !== 'brokerage'
          ),
          lockedFunds: TRADITIONAL_IRA.filter(
            (fund) => fund.lockedText || fund.terminated === true
          ).filter(
            (fund) =>
              fund.equityAsOf !== 0 &&
              fund.productType.toLowerCase() !== 'brokerage'
          ),
        },
        allFunds: [...filteredRolloeverIRA, ...filteredTraditionalIRA],
      };
    } else {
      return {
        ROLLOVER_IRA: {
          unlockedFunds: ROLLOVER_IRA.filter(
            (fund) =>
              !fund.lockedText ||
              fund.lockedText === EXISTING_AUTO_EXCHANGE ||
              fund.lockedText === EXISTING_AUTO_SELL
          ).filter(
            (fund) =>
              fund.equityAsOf !== 0 &&
              fund.terminated !== true &&
              fund.productType.toLowerCase() !== 'brokerage'
          ),
          lockedFunds: ROLLOVER_IRA.filter(
            (fund) =>
              (fund.lockedText !== null &&
                fund.lockedText !== undefined &&
                fund.lockedText !== EXISTING_AUTO_EXCHANGE &&
                fund.lockedText !== EXISTING_AUTO_SELL) ||
              fund.terminated === true
          ).filter(
            (fund) =>
              fund.equityAsOf !== 0 &&
              fund.productType.toLowerCase() !== 'brokerage'
          ),
        },
        TRADITIONAL_IRA: {
          unlockedFunds: TRADITIONAL_IRA.filter(
            (fund) =>
              !fund.lockedText ||
              fund.lockedText === EXISTING_AUTO_EXCHANGE ||
              fund.lockedText === EXISTING_AUTO_SELL
          ).filter(
            (fund) =>
              fund.equityAsOf !== 0 &&
              fund.terminated !== true &&
              fund.productType.toLowerCase() !== 'brokerage'
          ),
          lockedFunds: TRADITIONAL_IRA.filter(
            (fund) =>
              (fund.lockedText !== null &&
                fund.lockedText !== undefined &&
                fund.lockedText !== EXISTING_AUTO_EXCHANGE &&
                fund.lockedText !== EXISTING_AUTO_SELL) ||
              fund.terminated === true
          ).filter(
            (fund) =>
              fund.equityAsOf !== 0 &&
              fund.productType.toLowerCase() !== 'brokerage'
          ),
        },
        allFunds: [...filteredRolloeverIRA, ...filteredTraditionalIRA],
      };
    }
  }
);

export const getBankAssociatedWithIRAFunds = createSelector(
  getRMDfunds,
  getAssociatedAgreements,
  ({ allFunds }, associates) => {
    return allFunds.some((fund) => associates.includes(fund.uniqueKey));
  }
);

export const getBankAssociableWithIRAFunds = createSelector(
  getRMDfunds,
  getAssociableAgreements,
  ({ allFunds }, associates) => {
    return allFunds.some((fund) => associates.includes(fund.uniqueKey));
  }
);

export const getExistingRMDSWithAgreementParties = createSelector(
  getExistingRMDinfo,
  getUserRetirementFunds,
  getRMDAgreements,
  (existingRMDinfo, userRetirementFunds, rmdAgreements) => {
    let newRMDlist = existingRMDinfo;
    if (
      existingRMDinfo !== null &&
      existingRMDinfo !== undefined &&
      Object.keys(userRetirementFunds).length > 0
    ) {
      newRMDlist = existingRMDinfo.map((agreement) => {
        const rmdAgreementID = agreement.sourceAgreementKey.agreementID;
        const currRmdFund = rmdAgreements.find((rmdFund) => {
          return rmdFund.agreementID === rmdAgreementID;
        });
        if (userRetirementFunds.TRADITIONAL_IRA !== undefined) {
          const currFund = userRetirementFunds.TRADITIONAL_IRA.find((fund) => {
            return fund.agreementID === rmdAgreementID;
          });
          if (currFund) {
            return {
              ...agreement,
              ...currFund,
              ...currRmdFund,
            };
          }
        }
        if (userRetirementFunds.ROLLOVER_IRA !== undefined) {
          const currFund = userRetirementFunds.ROLLOVER_IRA.find((fund) => {
            return fund.agreementID === rmdAgreementID;
          });
          if (currFund) {
            return {
              ...agreement,
              ...currFund,
              ...currRmdFund,
            };
          }
        }
        if (userRetirementFunds.SEP_IRA !== undefined) {
          const currFund = userRetirementFunds.SEP_IRA.find((fund) => {
            return fund.agreementID === rmdAgreementID;
          });
          if (currFund) {
            return {
              ...agreement,
              ...currFund,
              ...currRmdFund,
            };
          }
        }
        return agreement;
      });
    }
    return newRMDlist;
  }
);

export const getOnlyOWNRRMDs = createSelector(
  getExistingRMDSWithAgreementParties,
  getUserBPID,
  (rmds, BPID) => {
    const res = rmds?.filter((agreements) => {
      if (agreements?.agreementParties) {
        const ownerArr = agreements?.agreementParties.filter(
          (roles) => roles.roleCode === 'OWNR'
        );
        return ownerArr.length > 0
          ? ownerArr[0].businessPartyID === BPID
          : false;
      }
      return false;
    });

    return res;
  }
);

export const getIsExistingRMDTrust = createSelector(getOnlyOWNRRMDs, (rmds) => {
  if (rmds && rmds.length > 0) {
    return rmds
      .filter(
        (rmd) =>
          rmd.type && rmd.type.enumerationID && rmd.type.enumerationID === 20
      )
      .some(
        (rmd) =>
          rmd.toFund &&
          rmd.toFund.agreementType &&
          rmd.toFund.agreementType === 'TRUST'
      );
  }
  return false;
});

export const getIsRMDSpecialPayee = createSelector(
  getOnlyOWNRRMDs,
  (systematicActivityRMDs) => {
    if (systematicActivityRMDs && systematicActivityRMDs.length > 0) {
      return systematicActivityRMDs.some((rmd) => rmd.specialPayee === true);
    }
    return false;
  }
);
export const getUserAllFunds = createSelector(
  getUserRetirementFunds,
  getUserNonRetirementFunds,
  (retirementFunds, nonRetirement) => {
    const allFunds = [];
    if (nonRetirement !== null && nonRetirement !== undefined) {
      Object.values(nonRetirement).forEach((type) => {
        type.forEach((fund) => {
          allFunds.push(fund);
        });
      });
    }
    if (retirementFunds !== null && retirementFunds !== undefined) {
      Object.values(retirementFunds).forEach((type) => {
        type.forEach((fund) => {
          allFunds.push(fund);
        });
      });
    }
    return allFunds;
  }
);

export const getIsRMDFundNonIRA = createSelector(
  getOnlyOWNRRMDs,
  (systematicActivityRMDs) => {
    if (systematicActivityRMDs && systematicActivityRMDs.length > 0) {
      return (
        systematicActivityRMDs.filter(
          (rmd) =>
            rmd.agreementType !== 'ROLLOVER_IRA' &&
            rmd.agreementType !== 'TRADITIONAL_IRA'
        ).length > 0
      );
    }
    return false;
  }
);

// Returns hidden RMD eligible IRA funds
export const getHiddenRMDFundIRAs = createSelector(
  getRMDAgreements,
  (rmdFundAgreements) => {
    const rmdIRAs = rmdFundAgreements.filter(
      (rmd) => {
        return (rmd.agreementType === "ROLLOVER_IRA" || rmd.agreementType === "TRADITIONAL_IRA") && rmd.hidden;
    });
    return rmdIRAs;
  }
);

export const getSystematicActivityRMDPercentage = createSelector(
  getOnlyOWNRRMDs,
  (systematicActivityRMDs) => {
    return _.keyBy(systematicActivityRMDs, 'agreementID');
  }
);

export const getRollOverSelectedPercent = createSelector(
  getRMDfunds,
  getSystematicActivityRMDPercentage,
  (rmdFunds, percent) => {
    return rmdFunds.ROLLOVER_IRA.unlockedFunds.map((fund) => {
      return percent[fund.agreementID]?.percentage;
    });
  }
);

export const getTraditionalSelectedPercent = createSelector(
  getRMDfunds,
  getSystematicActivityRMDPercentage,
  (rmdFunds, percent) => {
    return rmdFunds.TRADITIONAL_IRA.unlockedFunds.map((fund) => {
      return percent[fund.agreementID]?.percentage;
    });
  }
);

export const doesEverySystematicHaveAnAgreement = createSelector(
  getOnlyOWNRRMDs,
  getRMDAgreements,
  (systematic, agreement) => {
    const systematicMatchAgreement = systematic?.map((sys) => {
      return agreement?.some((fund) => {
        return sys?.agreementID === fund?.agreementID;
      });
    });
    return systematicMatchAgreement?.every((answer) => answer);
  }
);

export const doesUserLEGoToTrust = createSelector(
  getExistingRMDinfo,
  getUserFunds,
  (systematics, userFunds) => {
    const trustFunds = userFunds?.nonRetirementAgreements?.['TRUST']?.filter(
      (fund) => {
        return systematics
          ?.map((items) => {
            return items?.targetAgreementKey?.agreementID === fund?.agreementID;
          })
          .some((temp) => temp);
      }
    );
    return trustFunds?.length > 0;
  }
);

export const getRetirementFundsNotFilteredByType = createSelector(
  getUserRetirementFunds,
  (retirementFunds) => {
    return flatten(Object.values(retirementFunds));
  }
);

export const getRMDPlusAsOf = createSelector(
  getRMDAgreements,
  getRetirementFundsNotFilteredByType,
  (agreements, retirementFunds) => {
    let matchingFunds = agreements.map((agreement) => {
      return retirementFunds.map((fund) => {
        if (fund.agreementID === agreement.agreementID) {
          return { ...agreement, ...fund };
        } else {
          return null;
        }
      });
    });
    matchingFunds = flatten(matchingFunds).filter((fund) => fund !== null);
    return flatten(matchingFunds);
  }
);
